import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  AlertTriangle,
  Clock,
  FileText,
  ClipboardList,
  ShieldAlert,
  Users,
  Share2,
  Mail,
  CheckCheck,
  Check,
  X,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { useNotifications } from './useNotifications';
import { formatRelative } from '@/lib/formatters';
import { cn } from '@/lib/cn';
import type { Notification, NotificationCategory } from './types';

/** Width of the rendered dropdown panel in px. */
const PANEL_WIDTH_PX = 380;
/** Gap between trigger and panel. */
const PANEL_GAP_PX = 8;

/** Accent color per category (matches PRD §07 §4.2 category icon palette). */
const CATEGORY_ACCENT: Record<string, string> = {
  System:         'bg-sky-500',
  Orders:         'bg-rose-500',
  Reports:        'bg-emerald-500',
  SAB:            'bg-amber-500',
  ShareReport:    'bg-violet-500',
  UserManagement: 'bg-slate-500',
  ForgotPassword: 'bg-slate-500',
  UserInvite:     'bg-slate-500',
};
export interface NotificationsBellProps {
  /** Hide the inline "Notifications" label (used by compact header). */
  hideLabel?: boolean;
  /** Anchor alignment for the panel.
   *   "right" — bottom-right of trigger (default for top-right header bell)
   *   "left"  — right of trigger (used when the bell lives on a left rail)
   */
  panelAlign?: 'right' | 'left';
}

/**
 * NotificationsBell — In-App Bell surface (PRD §07).
 *
 * Renders a bell trigger with an unread count badge (capped at 99+),
 * and a portal'd popover containing up to 20 notifications grouped
 * by Today / Earlier. Each row supports inline mark-read, dismiss,
 * and open (deep-link → auto-mark-read per AC-IB-003).
 *
 * Loading, empty, and error states are all handled in-place per
 * §07.4.5; the error state surfaces a Retry control.
 *
 * Keyboard:
 *   • Trigger reachable via Tab. Enter / Space toggles open.
 *   • Esc closes the open popover.
 *   • Outside-click also closes.
 *   • Arrow Up / Down within the open popover cycles row focus.
 */
export function NotificationsBell({
  hideLabel = true,
  panelAlign = 'right',
}: NotificationsBellProps = {}) {
  const navigate = useNavigate();
  const {
    today,
    earlier,
    unreadCount,
    isLoading,
    isError,
    refetch,
    markRead,
    markAllRead,
    isMarkingAllRead,
    dismiss,
  } = useNotifications({ limit: 20 });

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);

  // Outside-click + Escape close
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Position the panel and keep it glued through scroll/resize.
  useEffect(() => {
    if (!open) return;
    function updatePos() {
      const btn = wrapperRef.current?.querySelector('button');
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const top = panelAlign === 'right'
        ? rect.bottom + PANEL_GAP_PX
        : rect.top;
      const rawLeft = panelAlign === 'right'
        ? rect.right - PANEL_WIDTH_PX
        : rect.right + PANEL_GAP_PX;
      const maxLeft = window.innerWidth - PANEL_WIDTH_PX - 12;
      const left = Math.min(Math.max(12, rawLeft), Math.max(12, maxLeft));
      setPanelPos({ top, left });
    }
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open, panelAlign]);

  // Keyboard cycling between rows when the popover is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const items = panelRef.current?.querySelectorAll<HTMLButtonElement>('[data-notif-row]');
      if (!items || items.length === 0) return;
      const arr = Array.from(items);
      const active = document.activeElement as HTMLElement | null;
      const idx = arr.findIndex((el) => el === active);
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        const next = arr[Math.min(idx + 1, arr.length - 1)] ?? arr[0];
        next?.focus();
      } else {
        const prev = arr[Math.max(idx - 1, 0)] ?? arr[0];
        prev?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  function toggleOpen() {
    setOpen((prev) => !prev);
  }

  function handleRowClick(row: Notification) {
    // AC-IB-003 — opening via deep link marks read.
    if (!row.readAt) markRead(row.notificationId);
    if (row.deepLink) {
      setOpen(false);
      navigate(row.deepLink);
    }
  }

  function handleSeeAll() {
    setOpen(false);
    // FR-160 — Alerts is the full inbox (same list as this bell).
    navigate('/alerts');
  }

  // §07.8 edge case: 99+ for large counts.
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  const panel = (open && panelPos) ? (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Notifications"
      style={{
        position: 'fixed',
        top: panelPos.top,
        left: panelPos.left,
        width: PANEL_WIDTH_PX,
        maxHeight: 'min(640px, calc(100vh - 24px))',
      }}
      className="z-[1000] flex flex-col overflow-hidden rounded-lg border border-border bg-background shadow-xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
          </p>
        </div>
        {unreadCount > 0 && !isError && (
          <button
            type="button"
            onClick={() => markAllRead()}
            disabled={isMarkingAllRead}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            title="Mark all as read"
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Mark all read
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto" role="list">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600" />
          </div>
        ) : isError ? (
          <div className="px-6 py-10 text-center">
            <p className="text-sm font-medium text-foreground">Couldn't load notifications</p>
            <p className="mt-1 text-xs text-muted-foreground">Network or server hiccup. Try again.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </button>
          </div>
        ) : today.length === 0 && earlier.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <Bell className="mx-auto h-8 w-8 text-muted-foreground/40" />
            <p className="mt-3 text-sm text-muted-foreground">You're all caught up.</p>
          </div>
        ) : (
          <>
            {today.length > 0 && (
              <Section label="Today">
                {today.map((row) => (
                  <NotificationRow
                    key={row.notificationId}
                    row={row}
                    onOpen={() => handleRowClick(row)}
                    onMarkRead={() => markRead(row.notificationId)}
                    onDismiss={() => dismiss(row.notificationId)}
                  />
                ))}
              </Section>
            )}
            {earlier.length > 0 && (
              <Section label="Earlier">
                {earlier.map((row) => (
                  <NotificationRow
                    key={row.notificationId}
                    row={row}
                    onOpen={() => handleRowClick(row)}
                    onMarkRead={() => markRead(row.notificationId)}
                    onDismiss={() => dismiss(row.notificationId)}
                  />
                ))}
              </Section>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-border px-3 py-2">
        <button
          type="button"
          onClick={handleSeeAll}
          className="flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          See all
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'
        }
        className={cn(
          'group relative inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors',
          'text-muted-foreground hover:bg-muted hover:text-foreground',
          open && 'bg-muted text-foreground',
        )}
      >
        <span className="relative inline-flex">
          <Bell className="h-5 w-5 shrink-0" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className={cn(
                'absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 leading-none text-white ring-2 ring-background',
                badgeLabel.length > 2 ? 'text-[8px]' : 'text-[9px]',
                'font-bold',
              )}
            >
              {badgeLabel}
            </span>
          )}
        </span>
        {!hideLabel && <span>Notifications</span>}
      </button>
      {panel && createPortal(panel, document.body)}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="sticky top-0 z-10 bg-background px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <ul className="divide-y divide-border/60">{children}</ul>
    </div>
  );
}

interface NotificationRowProps {
  row: Notification;
  onOpen: () => void;
  onMarkRead: () => void;
  onDismiss: () => void;
}

/**
 * Per-row icon selector. Returns a statically-referenced JSX node
 * for each branch so eslint's `react-hooks/static-components` rule
 * is satisfied (no dynamic component construction during render).
 *
 * Pattern dispatch on the title comes first (lease / measure) so
 * those domain phrases win over the category default.
 */
function RowIcon({ row }: { row: Notification }) {
  const iconClass = 'h-4 w-4';
  if (/lease expires/i.test(row.title)) return <AlertTriangle className={iconClass} />;
  if (/last measured/i.test(row.title)) return <Clock className={iconClass} />;
  switch (row.category) {
    case 'System':         return <ShieldAlert className={iconClass} />;
    case 'Orders':         return <ClipboardList className={iconClass} />;
    case 'Reports':        return <FileText className={iconClass} />;
    case 'SAB':            return <FileText className={iconClass} />;
    case 'ShareReport':    return <Share2 className={iconClass} />;
    case 'UserManagement': return <Users className={iconClass} />;
    case 'ForgotPassword': return <Mail className={iconClass} />;
    case 'UserInvite':     return <Mail className={iconClass} />;
    default:               return <Bell className={iconClass} />;
  }
}

function NotificationRow({ row, onOpen, onMarkRead, onDismiss }: NotificationRowProps) {
  const accent = CATEGORY_ACCENT[row.category as NotificationCategory] ?? 'bg-slate-400';
  const isUnread = !row.readAt;

  return (
    <li
      className={cn(
        'group/row relative flex gap-3 px-4 py-3 transition-colors hover:bg-muted',
        isUnread && 'bg-gray-50/40',
      )}
      role="listitem"
    >
      <button
        type="button"
        data-notif-row
        onClick={onOpen}
        className="absolute inset-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-inset"
        aria-label={`${row.title}${row.deepLink ? ' — open' : ''}`}
      />

      {isUnread && (
        <span className={cn('absolute left-0 top-0 h-full w-1', accent)} aria-hidden="true" />
      )}

      <div className={cn('mt-0.5 shrink-0 rounded-md p-1.5', isUnread ? 'text-foreground' : 'text-muted-foreground')}>
        <RowIcon row={row} />
      </div>

      <div className="min-w-0 flex-1">
        {row.title && (
          <p
            className={cn(
              'truncate text-sm',
              isUnread ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground',
            )}
          >
            {row.title}
          </p>
        )}
        <p className="line-clamp-2 text-xs text-muted-foreground">{row.message}</p>
        <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
          {formatRelative(row.createdAt)}
        </p>
      </div>

      <div className="relative z-10 flex shrink-0 items-start gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100">
        {isUnread && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead();
            }}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-emerald-50 hover:text-emerald-600 focus-visible:opacity-100"
            title="Mark as read"
            aria-label="Mark as read"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-500 focus-visible:opacity-100"
          title="Dismiss"
          aria-label="Dismiss notification"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}
